import { useState, useMemo, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Brain, CheckCircle2, XCircle, Edit3, ChevronDown, ChevronUp,
  Clock, Zap, TrendingUp, TrendingDown, Minus, AlertTriangle,
  Search, BarChart3, Shield, Activity, Target, Award,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAISignals, type AISignal } from '@/hooks/useAISignals';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import { useUsageLimits } from '@/hooks/useUsageLimits';
import { SoftUpgradePrompt } from '@/components/shared/SoftUpgradePrompt';
import { supabase } from '@/integrations/supabase/client';
import { Lock } from 'lucide-react';

/* ─── Terminal Card wrapper ─── */
const TCard = ({ className = '', children, accent }: { className?: string; children: React.ReactNode; accent?: string }) => (
  <div className={`rounded-lg border border-[hsl(var(--card-border))] bg-[hsl(var(--background-secondary))] ${accent ? `border-l-[3px] border-l-[hsl(var(${accent}))]` : ''} ${className}`}>
    {children}
  </div>
);

/* ─── Pipeline Stage ─── */
const pipelineStages = [
  { name: 'Market Scanner', icon: Search, active: true, lastRun: '2m ago' },
  { name: 'Regime Detector', icon: BarChart3, active: true, lastRun: '2m ago' },
  { name: 'AI Brain', icon: Brain, active: true, lastRun: '3m ago' },
  { name: 'Risk Check', icon: Shield, active: true, lastRun: '3m ago' },
  { name: 'Signal Output', icon: Zap, active: true, lastRun: '3m ago' },
];

const PipelineViz = () => (
  <TCard className="p-4">
    <div className="flex items-center gap-1.5 mb-4">
      <Activity className="w-3.5 h-3.5 text-[hsl(var(--accent-purple))]" />
      <span className="text-xs font-heading font-semibold uppercase tracking-wider text-muted-foreground">Agent Pipeline</span>
    </div>
    {/* Pipeline flow — horizontal on desktop, wrap on mobile */}
    <div className="flex items-center justify-start lg:justify-center gap-0 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-none">
      {pipelineStages.map((stage, i) => {
        const Icon = stage.icon;
        return (
          <div key={stage.name} className="flex items-center shrink-0">
            <div className={`flex flex-col items-center w-[100px] lg:w-[110px] py-3 px-2 rounded-md border border-[hsl(var(--card-border))] bg-[hsl(var(--background-tertiary))] snap-start ${stage.active ? 'ring-1 ring-[hsl(var(--accent-purple)/0.3)]' : ''}`}>
              <Icon className={`w-5 h-5 mb-1.5 ${stage.active ? 'text-[hsl(var(--accent-purple))]' : 'text-muted-foreground'}`} />
              <span className="text-[11px] font-semibold text-foreground text-center leading-tight">{stage.name}</span>
              <div className="flex items-center gap-1 mt-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${stage.active ? 'bg-[hsl(var(--accent-green))] animate-pulse' : 'bg-muted-foreground/40'}`} />
                <span className="text-[9px] text-muted-foreground">{stage.active ? 'Running' : 'Idle'}</span>
              </div>
              <span className="text-[9px] text-muted-foreground mt-0.5">{stage.lastRun}</span>
            </div>
            {/* Connector — dots on mobile, dashed line on desktop */}
            {i < pipelineStages.length - 1 && (
              <>
                <div className="hidden md:block w-6 lg:w-8 h-px relative mx-0.5 shrink-0">
                  <svg width="100%" height="8" viewBox="0 0 32 8" className="overflow-visible">
                    <line x1="0" y1="4" x2="32" y2="4" stroke="hsl(var(--accent-purple))" strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.5" />
                    <polygon points="28,1 32,4 28,7" fill="hsl(var(--accent-purple))" fillOpacity="0.5" />
                  </svg>
                </div>
                <div className="md:hidden flex items-center gap-0.5 mx-1 shrink-0">
                  <div className="w-1 h-1 rounded-full bg-[hsl(var(--accent-purple)/0.5)]" />
                  <div className="w-1 h-1 rounded-full bg-[hsl(var(--accent-purple)/0.4)]" />
                  <div className="w-1 h-1 rounded-full bg-[hsl(var(--accent-purple)/0.3)]" />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
    {/* Status bar */}
    <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-[hsl(var(--card-border))]">
      <span className="w-2 h-2 rounded-full bg-[hsl(var(--accent-green))]" />
      <span className="text-[11px] text-muted-foreground">Pipeline Status: <span className="text-[hsl(var(--accent-green))] font-medium">All agents operational</span></span>
    </div>
  </TCard>
);

/* ─── Confidence Bar ─── */
const ConfidenceBar = ({ value }: { value: number }) => {
  const pct = Math.round(value * 100);
  const barColor = value > 0.75 ? 'bg-[hsl(var(--accent-green))]' : value > 0.5 ? 'bg-[hsl(var(--accent-amber))]' : 'bg-[hsl(var(--accent-red))]';
  return (
    <div className="relative h-5 w-full rounded bg-[hsl(var(--background-tertiary))] overflow-hidden">
      <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono-data font-bold text-foreground">{pct}%</span>
    </div>
  );
};

/* ─── Risk Factor Pill ─── */
const riskPillColor = (rf: string) => {
  if (rf.includes('fed') || rf.includes('crisis') || rf.includes('halt')) return 'bg-[hsl(var(--accent-red)/0.15)] text-[hsl(var(--accent-red))] border-[hsl(var(--accent-red)/0.2)]';
  return 'bg-[hsl(var(--accent-amber)/0.15)] text-[hsl(var(--accent-amber))] border-[hsl(var(--accent-amber)/0.2)]';
};

/* ─── Signal Card ─── */
const SignalCard = ({
  signal,
  sideConfig,
  statusConfig,
  expanded,
  onToggleExpand,
  onApprove,
  onReject,
  onModify,
}: {
  signal: AISignal;
  sideConfig: Record<string, { color: string; icon: React.ElementType; label: string }>;
  statusConfig: Record<string, string>;
  expanded: boolean;
  onToggleExpand: () => void;
  onApprove: () => void;
  onReject: () => void;
  onModify: () => void;
}) => {
  const side = sideConfig[signal.side] || sideConfig.hold;
  const SideIcon = side.icon;
  const isExpired = signal.status === 'expired';
  const isApproved = signal.status === 'approved';
  const isPending = signal.status === 'pending';
  const posSize = signal.position_size_suggestion ?? 0;
  const maxAllowed = 622; // 5% of $12,440 example bankroll

  return (
    <TCard className={`transition-all animate-fade-in ${isExpired ? 'opacity-40' : ''}`}>
      {/* Header */}
      <div className="p-4 pb-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5">
            <span className="font-mono-data text-lg font-bold tracking-tight">{signal.symbol}</span>
            <Badge variant="outline" className={`text-[10px] font-bold ${side.color} ${isPending ? 'shadow-[0_0_8px_hsl(var(--accent-green)/0.3)]' : ''}`}>
              <SideIcon className="w-3 h-3 mr-1" />
              {side.label}
            </Badge>
            {isExpired && <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">Expired</Badge>}
            {isApproved && (
              <span className="flex items-center gap-1 text-[10px] text-[hsl(var(--accent-green))]">
                <CheckCircle2 className="w-3 h-3" /> Approved • Executing…
              </span>
            )}
          </div>
          <span className="text-[11px] text-muted-foreground font-mono-data">
            {formatDistanceToNow(new Date(signal.created_at), { addSuffix: true })}
          </span>
        </div>
        {/* Confidence */}
        <div className="mt-3">
          <ConfidenceBar value={signal.confidence} />
        </div>
      </div>

      {/* Body — two columns */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT: AI Reasoning */}
        <div>
          <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">AI Reasoning</span>
          <div className="rounded-md bg-[hsl(var(--background-tertiary))] p-3">
            <p className={`font-mono-data text-[12px] leading-relaxed text-muted-foreground ${!expanded ? 'line-clamp-4' : ''}`}>
              {signal.reasoning}
            </p>
            {signal.reasoning.length > 180 && (
              <button onClick={onToggleExpand} className="flex items-center gap-0.5 mt-1.5 text-[10px] text-[hsl(var(--accent-blue))] hover:underline">
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>

        {/* RIGHT: Risk Assessment */}
        <div>
          <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Risk Assessment</span>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Position Size</span>
              <span className="font-mono-data font-semibold">${posSize.toFixed(0)} <span className="text-muted-foreground font-normal">({((posSize / (maxAllowed / 0.05)) * 100).toFixed(1)}%)</span></span>
            </div>
            <Progress value={(posSize / maxAllowed) * 100} className="h-1.5 [&>div]:bg-[hsl(var(--accent-blue))]" />
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Max Allowed</span>
              <span className="font-mono-data text-muted-foreground">${maxAllowed} (5%)</span>
            </div>
            {/* Risk factors */}
            {signal.risk_factors && signal.risk_factors.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {signal.risk_factors.map((rf, i) => (
                  <Badge key={i} variant="outline" className={`text-[9px] ${riskPillColor(rf)}`}>
                    {rf.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer actions */}
      {isPending && (
        <div className="px-4 pb-4 flex flex-col md:flex-row items-stretch md:items-center gap-2">
          <Button size="sm" className="h-9 md:h-7 text-xs bg-[hsl(var(--accent-green))] hover:bg-[hsl(var(--accent-green)/0.8)] text-white w-full md:w-auto" onClick={onApprove}>
            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="h-9 md:h-7 text-xs border-[hsl(var(--accent-red)/0.3)] text-[hsl(var(--accent-red))] hover:bg-[hsl(var(--accent-red)/0.1)] w-full md:w-auto" onClick={onReject}>
            <XCircle className="w-3 h-3 mr-1" /> Reject
          </Button>
          <Button size="sm" variant="outline" className="h-9 md:h-7 text-xs border-[hsl(var(--accent-blue)/0.3)] text-[hsl(var(--accent-blue))] hover:bg-[hsl(var(--accent-blue)/0.1)] w-full md:w-auto" onClick={onModify}>
            <Edit3 className="w-3 h-3 mr-1" /> Modify
          </Button>
        </div>
      )}
    </TCard>
  );
};

/* ─── Main Page ─── */
const SignalReviewPage = () => {
  const { user } = useAuth();
  const { pendingSignals, recentSignals, isLoading, approveSignal, rejectSignal } = useAISignals();
  
  const { canApproveSignal, canAutoExecute, checkLimit } = useUsageLimits();
  const signalCheck = checkLimit('signals_approved');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [modifyingSignal, setModifyingSignal] = useState<AISignal | null>(null);
  const [modifiedSize, setModifiedSize] = useState(1);

  const [autoExecute, setAutoExecute] = useState(() => localStorage.getItem('atp-auto-execute') === 'true');
  const [autoThreshold, setAutoThreshold] = useState(() => parseFloat(localStorage.getItem('atp-auto-threshold') || '0.85'));

  // Countdown timer for next engine run
  const [countdown, setCountdown] = useState(15 * 60);
  useEffect(() => {
    const interval = setInterval(() => setCountdown(prev => (prev <= 0 ? 15 * 60 : prev - 1)), 1000);
    return () => clearInterval(interval);
  }, []);
  const countdownStr = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`;

  // Stats
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todaySignals = recentSignals.filter(s => new Date(s.created_at) >= today);
    const approved = todaySignals.filter(s => s.status === 'approved').length;
    const rejected = todaySignals.filter(s => s.status === 'rejected').length;
    const approvalRate = approved + rejected > 0 ? Math.round((approved / (approved + rejected)) * 100) : 0;
    const avgConfidence = todaySignals.length > 0
      ? Math.round((todaySignals.reduce((sum, s) => sum + s.confidence, 0) / todaySignals.length) * 100)
      : 0;
    const bestSignal = todaySignals.find(s => s.status === 'approved');
    return { total: todaySignals.length, approved, approvalRate, avgConfidence, bestSignal };
  }, [recentSignals]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleAutoExecuteToggle = (checked: boolean) => {
    setAutoExecute(checked);
    localStorage.setItem('atp-auto-execute', String(checked));
  };
  const handleThresholdChange = (value: number[]) => {
    setAutoThreshold(value[0]);
    localStorage.setItem('atp-auto-threshold', String(value[0]));
  };

  const handleApprove = async (signalId: string) => {
    if (!user) return;
    if (!canApproveSignal) {
      toast.error('Signal approval limit reached for this month');
      return;
    }
    try {
      await supabase.rpc('increment_usage', { p_user_id: user.id, p_metric: 'signals_approved' });
      await approveSignal(signalId, user.id);
      toast.success('Signal approved');
    }
    catch { toast.error('Failed to approve signal'); }
  };
  const handleReject = async () => {
    if (!rejectingId || !user) return;
    try { await rejectSignal(rejectingId, user.id); toast.success('Signal rejected'); setRejectingId(null); setRejectReason(''); }
    catch { toast.error('Failed to reject signal'); }
  };
  const handleModifyApprove = async () => {
    if (!modifyingSignal || !user) return;
    const newSize = parseFloat(String(modifiedSize));
    if (!Number.isFinite(newSize) || newSize <= 0) {
      toast.error('Enter a valid position size');
      return;
    }
    try {
      await approveSignal(modifyingSignal.id, user.id, newSize);
      toast.success(`Signal approved with size ${newSize}`);
      setModifyingSignal(null);
    }
    catch { toast.error('Failed to approve modified signal'); }
  };

  const sideConfig: Record<string, { color: string; icon: React.ElementType; label: string }> = {
    buy: { color: 'bg-[hsl(var(--accent-green)/0.15)] text-[hsl(var(--accent-green))] border-[hsl(var(--accent-green)/0.2)]', icon: TrendingUp, label: 'BUY' },
    sell: { color: 'bg-[hsl(var(--accent-red)/0.15)] text-[hsl(var(--accent-red))] border-[hsl(var(--accent-red)/0.2)]', icon: TrendingDown, label: 'SELL' },
    hold: { color: 'bg-muted text-muted-foreground border-border', icon: Minus, label: 'HOLD' },
  };

  const statusConfig: Record<string, string> = {
    pending: 'bg-[hsl(var(--accent-amber)/0.15)] text-[hsl(var(--accent-amber))] border-[hsl(var(--accent-amber)/0.2)] animate-pulse',
    approved: 'bg-[hsl(var(--accent-green)/0.15)] text-[hsl(var(--accent-green))] border-[hsl(var(--accent-green)/0.2)]',
    rejected: 'bg-[hsl(var(--accent-red)/0.15)] text-[hsl(var(--accent-red))] border-[hsl(var(--accent-red)/0.2)]',
    executed: 'bg-[hsl(var(--accent-blue)/0.15)] text-[hsl(var(--accent-blue))] border-[hsl(var(--accent-blue)/0.2)]',
    expired: 'bg-muted text-muted-foreground border-border',
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex items-center gap-2.5">
        <Brain className="w-5 h-5 text-[hsl(var(--accent-purple))]" />
        <h2 className="text-xl font-heading font-semibold tracking-tight">AI Signal Review</h2>
      </div>

      {/* Pipeline visualization */}
      <PipelineViz />

      {/* Main content: signals + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        {/* Signal Cards */}
        <div className="space-y-3 min-w-0">
          {isLoading ? (
            <div className="text-center py-16 text-muted-foreground text-sm">Loading signals…</div>
          ) : recentSignals.length === 0 ? (
            <TCard className="py-16 text-center">
              <Brain className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-muted-foreground text-sm">No AI signals yet. The engine generates signals every 15 minutes.</p>
              <p className="text-muted-foreground text-xs mt-1 font-mono-data">Next run in {countdownStr}</p>
            </TCard>
          ) : (
            recentSignals.map(signal => (
              <SignalCard
                key={signal.id}
                signal={signal}
                sideConfig={sideConfig}
                statusConfig={statusConfig}
                expanded={expandedIds.has(signal.id)}
                onToggleExpand={() => toggleExpand(signal.id)}
                onApprove={() => handleApprove(signal.id)}
                onReject={() => setRejectingId(signal.id)}
                onModify={() => { setModifyingSignal(signal); setModifiedSize(signal.position_size_suggestion); }}
              />
            ))
          )}
        </div>

        {/* Right Sidebar */}
        <div className="hidden lg:flex flex-col gap-4">
          {/* Signal Stats Today */}
          <TCard className="p-4" accent="--accent-blue">
            <div className="flex items-center gap-1.5 mb-3">
              <Target className="w-3.5 h-3.5 text-[hsl(var(--accent-blue))]" />
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-muted-foreground">Signal Stats Today</span>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Total Signals', value: stats.total },
                { label: 'Approved', value: `${stats.approved} (${stats.approvalRate}%)` },
                { label: 'Avg Confidence', value: `${stats.avgConfidence}%` },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-mono-data font-semibold">{row.value}</span>
                </div>
              ))}
              {stats.bestSignal && (
                <div className="text-[10px] text-muted-foreground pt-1 border-t border-[hsl(var(--card-border))]">
                  Best: <span className="text-[hsl(var(--accent-green))] font-semibold">{stats.bestSignal.side.toUpperCase()} {stats.bestSignal.symbol}</span>
                </div>
              )}
            </div>
          </TCard>

          {/* Next Run Timer */}
          <TCard className="p-4" accent="--accent-purple">
            <div className="flex items-center gap-1.5 mb-2">
              <Clock className="w-3.5 h-3.5 text-[hsl(var(--accent-purple))]" />
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-muted-foreground">Next Engine Run</span>
            </div>
            <div className="font-mono-data text-2xl font-bold text-center py-2">{countdownStr}</div>
          </TCard>

          {/* Auto-Execute Toggle */}
          <TCard className="p-4" accent="--accent-amber">
            <div className="flex items-center gap-1.5 mb-3">
              <Zap className="w-3.5 h-3.5 text-[hsl(var(--accent-amber))]" />
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-muted-foreground">Auto-Execute</span>
            </div>
            {canAutoExecute ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-muted-foreground">Enabled</span>
                  <Switch checked={autoExecute} onCheckedChange={handleAutoExecuteToggle} />
                </div>
                {autoExecute && (
                  <div className="space-y-2 pt-2 border-t border-[hsl(var(--card-border))]">
                    <span className="text-[10px] text-muted-foreground">Auto-approve above:</span>
                    <Slider min={0.7} max={0.95} step={0.05} value={[autoThreshold]} onValueChange={handleThresholdChange} />
                    <span className="text-sm font-mono-data font-bold block text-center">{Math.round(autoThreshold * 100)}%</span>
                    <div className="flex items-center gap-1 text-[9px] text-[hsl(var(--accent-amber))]">
                      <AlertTriangle className="w-3 h-3" />
                      <span>Signals above threshold auto-approved</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Lock className="w-3.5 h-3.5" />
                <span>Auto-execute available on Quant tier</span>
              </div>
            )}
          </TCard>

          {/* Signal limit warning */}
          {!canApproveSignal && (
            <SoftUpgradePrompt
              feature="signal approvals"
              message={signalCheck.message || ''}
              currentUsage={signalCheck.used}
              limit={signalCheck.limit}
            />
          )}

          {/* Model Performance */}
          <TCard className="p-4" accent="--accent-green">
            <div className="flex items-center gap-1.5 mb-3">
              <Award className="w-3.5 h-3.5 text-[hsl(var(--accent-green))]" />
              <span className="text-[10px] font-heading font-semibold uppercase tracking-wider text-muted-foreground">Model Performance</span>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Win Rate (30d)', value: '68.4%' },
                { label: 'Avg Return/Signal', value: '+1.2%' },
                { label: 'Model Accuracy', value: '68.4%' },
              ].map(row => (
                <div key={row.label} className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-mono-data font-semibold text-[hsl(var(--accent-green))]">{row.value}</span>
                </div>
              ))}
            </div>
          </TCard>
        </div>
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectingId} onOpenChange={() => { setRejectingId(null); setRejectReason(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Reject Signal</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason" className="text-sm">Reason (optional)</Label>
            <Input id="reject-reason" placeholder="Why are you rejecting this signal?" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setRejectingId(null)}>Cancel</Button>
            <Button size="sm" variant="destructive" onClick={handleReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modify dialog */}
      <Dialog open={!!modifyingSignal} onOpenChange={() => setModifyingSignal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Modify & Approve</DialogTitle></DialogHeader>
          {modifyingSignal && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium">{modifyingSignal.symbol} — {modifyingSignal.side.toUpperCase()}</p>
                <p className="text-xs text-muted-foreground mt-1">{modifyingSignal.reasoning}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Position Size</Label>
                <div className="flex items-center gap-3">
                  <Slider min={0.1} max={3} step={0.1} value={[modifiedSize]} onValueChange={v => setModifiedSize(v[0])} className="flex-1" />
                  <span className="text-sm font-mono-data font-medium w-10">{modifiedSize.toFixed(1)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setModifyingSignal(null)}>Cancel</Button>
            <Button size="sm" className="bg-[hsl(var(--accent-green))] hover:bg-[hsl(var(--accent-green)/0.8)] text-white" onClick={handleModifyApprove}>
              Approve with changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignalReviewPage;
